import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

// Public pages required by Google's OAuth consent screen (#763): privacy policy and terms of service. No sign-in.

const UPDATED = '7 October 2026';

function LegalPage({ title, children, other }: { title: string; children: ReactNode; other: { to: string; label: string } }) {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
      <main className="mx-auto max-w-[68ch] px-4 pb-20 pt-12 sm:pt-16">
        <Link to="/login" className="inline-block focus-visible:outline focus-visible:outline-2" aria-label="aidev">
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 34, color: 'var(--color-text)', letterSpacing: '0.04em', lineHeight: 1 }}>AI</span>
          <span className="ml-1" style={{ fontFamily: 'var(--font-display)', fontSize: 13, color: 'var(--color-brand)', letterSpacing: '0.25em' }}>DEV</span>
        </Link>
        <h1 className="mt-8 text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>{title}</h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-3)' }}>FD Management Sàrl-s, Luxembourg. Last updated {UPDATED}.</p>
        <div className="legal mt-8 space-y-6 text-[15px] leading-[1.65]" style={{ color: 'var(--color-text-2)' }}>{children}</div>
        <nav className="mt-12 flex gap-6 text-sm" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 16 }}>
          <Link to={other.to} className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>{other.label}</Link>
          <Link to="/login" className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>Sign in</Link>
        </nav>
      </main>
    </div>
  );
}

function H2({ children }: { children: ReactNode }) {
  return <h2 className="pt-2 text-base font-semibold" style={{ color: 'var(--color-text)' }}>{children}</h2>;
}

const READS: Array<{ data: string; read: string; why: string }> = [
  { data: 'Start and end times of your calendar events, for the next and last 12 hours', read: 'Yes', why: 'Know whether you are in a meeting, so notifications wait' },
  { data: 'Event type (meeting, focus time, out of office), whether it is marked free, whether you declined it', read: 'Yes', why: 'Tell a meeting from focus time or an absence' },
  { data: 'Your Google account address', read: 'Yes', why: 'Show which account is connected' },
  { data: 'Event titles, descriptions, locations and attendees, in the period you ask about', read: 'When you ask', why: 'Answer what you ask an aidev session about your calendar' },
  { data: 'Event attachments', read: 'No', why: 'Never requested from Google' },
  { data: 'Email (Gmail) and files (Drive)', read: 'Not today', why: 'Only if a later version asks for it, with a new consent' },
];

export function Privacy() {
  return (
    <LegalPage title="Privacy policy" other={{ to: '/terms', label: 'Terms of service' }}>
      <p>
        aidev is the private operations tool of FD Management Sàrl-s. It is used by its operator and by people of client
        organisations invited to it; it is not offered to the public. This page says what aidev does with personal data,
        in particular with Google account data.
      </p>

      <H2>Google account data</H2>
      <p>
        An administrator connects a server of aidev to their Google account in a client&apos;s Workspace. aidev then
        reads, from the primary calendar of that account (and from the other calendars only what Google shows in it):
      </p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[32rem] text-left text-sm">
          <thead>
            <tr style={{ color: 'var(--color-text)', borderBottom: '1px solid var(--color-border-2)' }}>
              <th className="py-2 pr-4 font-medium">Data</th>
              <th className="py-2 pr-4 font-medium">Read</th>
              <th className="py-2 font-medium">Why</th>
            </tr>
          </thead>
          <tbody>
            {READS.map((r) => (
              <tr key={r.data} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td className="py-2 pr-4 align-top">{r.data}</td>
                <td className="py-2 pr-4 align-top font-medium" style={{ color: r.read === 'Yes' ? 'var(--color-text)' : 'var(--color-text-3)' }}>{r.read}</td>
                <td className="py-2 align-top">{r.why}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        Event times are used to decide when to send notifications; they are kept in memory for a few minutes and are not
        stored. Event details are read only when you ask a session of aidev about your calendar: the session (an AI
        assistant, Claude, run through Amazon Bedrock in the same AWS region) sees them for that conversation, which aidev
        keeps like any other session.
      </p>
      <p>
        aidev changes your calendar (adds, changes or deletes an event) only when you approve that change in aidev. A
        session can only propose a change: aidev shows it to you, and applies it with Google once you approve it, never
        before. A proposed change is kept 7 days.
      </p>
      <p>
        aidev does not use Google data for advertising, does not sell it, transfers it only to provide what you ask
        for, and does not use it to train artificial intelligence models; nor does Amazon Bedrock.
      </p>
      <p>
        aidev&apos;s use and transfer of information received from Google APIs adheres to the{' '}
        <a href="https://developers.google.com/terms/api-services-user-data-policy" className="underline" style={{ color: 'var(--color-brand-light)' }}>
          Google API Services User Data Policy
        </a>, including the Limited Use requirements.
      </p>

      <H2>Where it is kept, and who can see it</H2>
      <p>
        aidev runs on Amazon Web Services in Ireland (eu-west-1). The access granted by Google (a token) is stored
        encrypted with a key dedicated to each server, used only by aidev&apos;s API, and denied to everything else,
        including the agents aidev runs. Only the operator of FD Management Sàrl-s administers aidev.
      </p>

      <H2>How long</H2>
      <p>
        The Google token is kept until the connection is removed. Records of notifications sent are kept 40 days, the
        decisions you set aside 30 days, proposed calendar changes 7 days. Your aidev account is kept while you use aidev.
      </p>

      <H2>Removing access</H2>
      <p>
        Click Disconnect on the server page in aidev: the token is revoked at Google and deleted. You can also remove
        aidev from your Google account at{' '}
        <a href="https://myaccount.google.com/permissions" className="underline" style={{ color: 'var(--color-brand-light)' }}>myaccount.google.com/permissions</a>.
      </p>

      <H2>Other data</H2>
      <p>
        To sign in, aidev keeps your email address and role. It records what you do in aidev (sessions, decisions,
        notifications) to run the service. Notifications to your devices go through your browser&apos;s push service.
      </p>

      <H2>Your rights and contact</H2>
      <p>
        Under the GDPR you can ask to access, correct or delete your data, and complain to the Commission nationale pour
        la protection des données (Luxembourg). Write to{' '}
        <a href="mailto:fred@fd-management.net" className="underline" style={{ color: 'var(--color-brand-light)' }}>fred@fd-management.net</a>.
      </p>
    </LegalPage>
  );
}

export function Terms() {
  return (
    <LegalPage title="Terms of service" other={{ to: '/privacy', label: 'Privacy policy' }}>
      <p>
        These terms apply to aidev, the private operations tool of FD Management Sàrl-s (Luxembourg). By signing in, you
        accept them.
      </p>

      <H2>Who may use aidev</H2>
      <p>
        aidev is not a public service. Only the operator of FD Management Sàrl-s and people invited by them, for the
        work they do together, may use it. Accounts are created by an administrator.
      </p>

      <H2>Acceptable use</H2>
      <p>
        Use aidev only for that work, with accounts and data you are allowed to use. Do not try to reach data or servers
        you were not given, to get around its protections, or to use it against the law or anyone&apos;s rights.
      </p>

      <H2>Connected accounts</H2>
      <p>
        When you connect an account (such as Google), you allow aidev to use it as described in the{' '}
        <Link to="/privacy" className="underline" style={{ color: 'var(--color-brand-light)' }}>privacy policy</Link>, and you can
        remove that access at any time.
      </p>

      <H2>No warranty</H2>
      <p>
        aidev is provided as it is, without warranty of availability or fitness for a purpose. What it prepares (reviews,
        analyses, drafts) is a proposal: decisions remain yours.
      </p>

      <H2>Liability</H2>
      <p>
        To the extent the law allows, FD Management Sàrl-s is not liable for indirect damage arising from the use of
        aidev. Nothing in these terms limits liability that cannot be limited by law.
      </p>

      <H2>Changes and law</H2>
      <p>
        These terms may change; the date above shows the current version. They are governed by Luxembourg law, and the
        courts of Luxembourg have jurisdiction.
      </p>

      <H2>Contact</H2>
      <p>
        <a href="mailto:fred@fd-management.net" className="underline" style={{ color: 'var(--color-brand-light)' }}>fred@fd-management.net</a>
      </p>
    </LegalPage>
  );
}
